import React from "react";
import { motion } from "framer-motion";
import { UserPlus, Cog, BarChart3, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Chapter from "../site/Chapter";

const pillars = [
  {
    icon: UserPlus,
    tag: "RPO",
    title: "Build your team",
    body: "AI-assisted sourcing and screening, run by recruiters who make every final call — so you get faster shortlists without lower quality.",
    to: "/services/rpo",
    ctaLabel: "Explore RPO",
    accent: "#00E5FF",
  },
  {
    icon: Cog,
    tag: "BPO",
    title: "Run your operations",
    body: "Customer support, admin, and industry-specific back-office work handled by trained specialists, with AI-assisted tools speeding up the routine parts.",
    to: "/services/bpo",
    ctaLabel: "Explore BPO",
    accent: "#A3FF5E",
  },
  {
    icon: BarChart3,
    tag: "KPO",
    title: "Know your numbers",
    body: "Data processing, research, and analytics turned into something you can act on — not just a bigger spreadsheet.",
    to: "/services/kpo",
    ctaLabel: "Explore KPO",
    accent: "#007AFF",
  },
];

export default function HomePillars() {
  return (
    <section
      id="pillars"
      data-testid="pillars-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <Chapter
            number="03"
            eyebrow="How We Help You Scale"
            title={
              <>
                Three pillars.{" "}
                <span className="font-serif italic">One</span> operating partner.
              </>
            }
          />
          <p className="text-slate-400 text-base lg:max-w-sm">
            Start with one function. Expand as it works. Most clients begin
            with one pillar and grow into another within six months.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.tag}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`pillar-card-${p.tag.toLowerCase()}`}
              className="group relative rounded-2xl border border-slate-800 bg-gradient-to-b from-[#0A1325] to-[#070F1F] p-8 hover:border-[#00E5FF]/50 transition-all overflow-hidden flex flex-col"
            >
              <div
                className="absolute -top-24 -right-24 h-56 w-56 rounded-full opacity-[0.10] blur-3xl"
                style={{ background: p.accent }}
              />
              <div className="flex items-start justify-between">
                <div
                  className="inline-flex h-12 w-12 items-center justify-center rounded-xl border"
                  style={{
                    borderColor: `${p.accent}40`,
                    background: `${p.accent}12`,
                    color: p.accent,
                  }}
                >
                  <p.icon size={22} />
                </div>
                <span
                  className="font-display text-xs font-bold tracking-[0.24em] uppercase"
                  style={{ color: p.accent }}
                >
                  {p.tag}
                </span>
              </div>

              <h3 className="font-display mt-8 text-2xl sm:text-3xl font-semibold text-white leading-tight">
                {p.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-400 flex-1">
                {p.body}
              </p>

              <Link
                to={p.to}
                data-testid={`pillar-cta-${p.tag.toLowerCase()}`}
                className="group/link mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[#00E5FF] transition-colors"
              >
                {p.ctaLabel}
                <ArrowUpRight size={16} className="arrow-shift" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
